/**
 * Banner cookie integrato, per i siti che non hanno un proprio componente
 * (siti statici, siti fatti da terzi, builder senza consent manager).
 *
 * Si attiva con data-banner="true" sul tag <script>. Segue lo standard Emerge
 * (skill privacy-policy-italia, references/cookie-banner-spec.md): card in basso
 * al centro (max 672px) su sfondo scurito che non chiude, tre bottoni paritari
 * [Accetta tutti] [Rifiuta tutti] [Preferenze], modale centrata con un
 * interruttore per categoria, Necessari sempre attivi, Esc chiude solo la
 * modale, focus intrappolato, link permanente via window.EmergePrivacy.openPreferences().
 *
 * Configurazione (attributi del tag, oppure window.EMERGE_PRIVACY_BANNER):
 *   data-categories="analytics,marketing,functional"  categorie in uso (senza, il banner non compare)
 *                                                      la modale elenca comunque tutte le categorie: quelle non in uso sono disattivate, con nota
 *   data-notice="true"                                 banner visibile anche senza categorie da consentire
 *   data-lang="it|en|de"                               lingua dei testi (default: lang del documento, poi it)
 *   data-privacy-url="/privacy" data-cookie-url="/cookie"
 *   data-accent="#7a1f2b"                              colore di focus e interruttori
 *
 * Blocco dei contenuti fino al consenso:
 *   <script type="text/plain" data-emerge-category="marketing" src="..."></script>
 *   <iframe data-emerge-src="https://www.youtube.com/embed/..." data-emerge-category="marketing"></iframe>
 *   <div data-emerge-placeholder-for="marketing">Contenuto disponibile dopo il consenso.</div>
 * Alla concessione della categoria lo script viene eseguito, l'iframe caricato, il segnaposto nascosto.
 */

import type { ConsentCategories, ConsentPublicationReference, ConsentState, EmergePrivacyAPI } from './types';

export interface BannerConfig {
  categories: string[];
  /** Mostra l’avviso legacy anche senza categorie facoltative. */
  notice?: boolean;
  lang: string;
  privacyUrl: string;
  cookieUrl: string;
  accent: string;
  enabled?: boolean;
  publication?: ConsentPublicationReference;
}

const ERRORE_REGISTRAZIONE: Record<string, string> = {
  it: 'Registrazione non riuscita. I servizi facoltativi restano disattivati. Puoi riprovare dalle preferenze.',
  en: 'Your choice could not be recorded. Optional services remain disabled. You can try again from preferences.',
  de: 'Ihre Auswahl konnte nicht gespeichert werden. Optionale Dienste bleiben deaktiviert. Sie können es in den Einstellungen erneut versuchen.',
};
const ERRORE_PUBBLICAZIONE: Record<string, string> = {
  it: 'Le preferenze sono temporaneamente non disponibili. I servizi facoltativi restano disattivati. Riprova ricaricando la pagina.',
  en: 'Preferences are temporarily unavailable. Optional services remain disabled. Please reload the page to try again.',
  de: 'Die Einstellungen sind vorübergehend nicht verfügbar. Optionale Dienste bleiben deaktiviert. Bitte laden Sie die Seite erneut.',
};

export function mostraErrorePubblicazione(api: EmergePrivacyAPI, lang: string): void {
  const show = () => {
    let host = document.getElementById('emerge-privacy-ui');
    if (!host) { host = document.createElement('div'); host.id = 'emerge-privacy-ui'; document.body.appendChild(host); }
    host.textContent = ERRORE_PUBBLICAZIONE[lang] ?? ERRORE_PUBBLICAZIONE.en;
    host.setAttribute('role', 'status');
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = lang === 'it' ? 'Rifiuta tutti' : lang === 'de' ? 'Alle ablehnen' : 'Reject all';
    button.addEventListener('click', () => { void api.updateConsent({ categories: { necessary: true }, source: 'preferences' }); });
    host.appendChild(button);
  };
  window.addEventListener('emerge-privacy-open-preferences', show);
  document.addEventListener('click', (event) => {
    if ((event.target as Element | null)?.closest?.('[data-emerge-preferences]')) { event.preventDefault(); show(); }
  });
  if (document.body) show();
  else document.addEventListener('DOMContentLoaded', show, { once: true });
}

type Testi = {
  title: string;
  description: (privacy: string, cookie: string) => string;
  acceptAll: string;
  rejectAll: string;
  preferences: string;
  modalTitle: string;
  close: string;
  save: string;
  always: string;
  notUsed: string;
  categories: Record<string, { name: string; help: string }>;
};

const TESTI: Record<string, Testi> = {
  it: {
    title: 'Cookie e strumenti di tracciamento',
    description: (p, c) =>
      `Questo sito usa cookie tecnici e, solo con il tuo consenso, altri strumenti per le finalità elencate nelle preferenze. Puoi accettare, rifiutare o scegliere per categoria, e cambiare idea in ogni momento dal collegamento "Preferenze cookie" a fondo pagina. Informazioni: <a href="${p}">Privacy</a> e <a href="${c}">Cookie policy</a>.`,
    acceptAll: 'Accetta tutti',
    rejectAll: 'Rifiuta tutti',
    preferences: 'Preferenze',
    modalTitle: 'Preferenze cookie',
    close: 'Chiudi le preferenze',
    save: 'Salva le preferenze',
    always: 'sempre attivi',
    notUsed: 'Non in uso su questo sito: nessuno strumento di questa categoria è installato.',
    categories: {
      necessary: { name: 'Necessari', help: 'Servono al funzionamento del sito e non richiedono consenso.' },
      analytics: { name: 'Statistiche', help: 'Misuriamo le visite per capire come viene usato il sito.' },
      marketing: { name: 'Marketing', help: 'Contenuti e annunci di terze parti: video, social, pubblicità.' },
      functional: { name: 'Funzionalità', help: 'Preferenze e servizi aggiuntivi, come mappe e chat.' },
    },
  },
  en: {
    title: 'Cookies and tracking tools',
    description: (p, c) =>
      `This site uses technical cookies and, only with your consent, other tools for the purposes listed in the preferences. You can accept, reject or choose by category, and change your mind at any time from the "Cookie preferences" link at the bottom of the page. Information: <a href="${p}">Privacy</a> and <a href="${c}">Cookie policy</a>.`,
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    preferences: 'Preferences',
    modalTitle: 'Cookie preferences',
    close: 'Close preferences',
    save: 'Save preferences',
    always: 'always on',
    notUsed: 'Not in use on this site: no tool of this category is installed.',
    categories: {
      necessary: { name: 'Necessary', help: 'Required for the site to work; no consent needed.' },
      analytics: { name: 'Statistics', help: 'We measure visits to understand how the site is used.' },
      marketing: { name: 'Marketing', help: 'Third-party content and ads: videos, social media, advertising.' },
      functional: { name: 'Functionality', help: 'Preferences and extra services such as maps and chat.' },
    },
  },
  de: {
    title: 'Cookies und Tracking-Tools',
    description: (p, c) =>
      `Diese Website verwendet technische Cookies und, nur mit Ihrer Einwilligung, weitere Tools für die in den Einstellungen genannten Zwecke. Sie können alle akzeptieren, alle ablehnen oder nach Kategorie wählen und Ihre Entscheidung jederzeit über den Link "Cookie-Einstellungen" am Seitenende ändern. Informationen: <a href="${p}">Datenschutz</a> und <a href="${c}">Cookie-Richtlinie</a>.`,
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Alle ablehnen',
    preferences: 'Einstellungen',
    modalTitle: 'Cookie-Einstellungen',
    close: 'Einstellungen schließen',
    save: 'Einstellungen speichern',
    always: 'immer aktiv',
    notUsed: 'Auf dieser Website nicht in Verwendung: kein Dienst dieser Kategorie ist installiert.',
    categories: {
      necessary: { name: 'Notwendig', help: 'Für den Betrieb der Website erforderlich, keine Einwilligung nötig.' },
      analytics: { name: 'Statistik', help: 'Wir messen Besuche, um die Nutzung der Website zu verstehen.' },
      marketing: { name: 'Marketing', help: 'Inhalte und Anzeigen Dritter: Videos, soziale Medien, Werbung.' },
      functional: { name: 'Funktional', help: 'Einstellungen und Zusatzdienste wie Karten und Chat.' },
    },
  },
};

const STILE = `
  :host { all: initial; font: inherit; }
  * { box-sizing: border-box; }
  .velo { position: fixed; inset: 0; background: rgba(0,0,0,.3); z-index: 9999; }
  .cornice { position: fixed; inset: 0; display: flex; align-items: flex-end; justify-content: center; padding: 16px; pointer-events: none; z-index: 9999; }
  .cornice.centro { align-items: center; z-index: 10000; }
  .scheda { pointer-events: auto; width: 100%; max-width: 672px; max-height: 90vh; overflow-y: auto; background: #fff; color: #111;
    border: 1px solid #d9d9d9; border-radius: 8px; box-shadow: 0 20px 50px rgba(0,0,0,.25); padding: 24px; margin-bottom: 16px;
    font-family: inherit; font-size: 15px; line-height: 1.5; }
  .scheda.modale { max-width: 600px; padding: 32px; margin-bottom: 0; position: relative; }
  @media (min-width: 640px) { .scheda { margin-bottom: 32px; } }
  h2 { margin: 0 0 8px; font-size: 18px; font-weight: 600; line-height: 1.3; }
  p { margin: 0 0 16px; }
  a { color: inherit; text-decoration: underline; }
  .bottoni { display: flex; flex-direction: column; gap: 8px; }
  @media (min-width: 768px) { .bottoni { flex-direction: row; } .bottoni button { flex: 1 1 0; } }
  button { font: inherit; font-weight: 600; min-height: 44px; padding: 10px 16px; border: 1px solid #111; border-radius: 6px;
    background: #fff; color: #111; cursor: pointer; width: 100%; }
  button:hover { background: #f3f3f3; }
  button:focus-visible, .interruttore:focus-visible, a:focus-visible { outline: 2px solid var(--accento); outline-offset: 2px; }
  .chiudi { position: absolute; top: 12px; right: 12px; width: 40px; min-height: 40px; padding: 0; border: 0; background: transparent; font-size: 22px; line-height: 1; }
  .categoria { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 12px 0; border-top: 1px solid #e5e5e5; }
  .categoria:first-of-type { border-top: 0; }
  .categoria strong { display: block; font-weight: 600; }
  .categoria small { display: block; color: #444; font-size: 13px; }
  .categoria small.nota { color: #666; font-size: 12px; font-style: italic; margin-top: 2px; }
  .categoria.inattiva strong { color: #666; }
  .interruttore { flex: 0 0 auto; width: 48px; min-height: 28px; height: 28px; padding: 0; border-radius: 14px; border: 1px solid #8a8a8a; background: #cfcfcf; position: relative; cursor: pointer; }
  .interruttore::after { content: ""; position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 50%; background: #fff; transition: left .15s; }
  .interruttore[aria-checked="true"] { background: var(--accento); border-color: var(--accento); }
  .interruttore[aria-checked="true"]::after { left: 23px; }
  .interruttore[aria-disabled="true"] { opacity: .6; cursor: default; }
  .azioni { display: flex; flex-direction: column; gap: 8px; margin-top: 20px; }
  @media (min-width: 640px) { .azioni { flex-direction: row; } .azioni button { flex: 1 1 0; } }
`;

const CATEGORIE_NOTE = ['analytics', 'marketing', 'functional'];

/** Gli indirizzi delle informative finiscono in un attributo href: niente virgolette o <> dentro. */
const attributo = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/**
 * La configurazione del banner sta nel CRM (policy_data.banner del sito): il tag
 * nel sito porta solo data-tid. Gli attributi del tag, se presenti, vincono sul
 * CRM (servono alle prove e ai casi particolari). Timeout breve: il banner non
 * deve aspettare la rete piu' di tanto.
 */
export async function caricaConfigBannerRemota(tid: string | null, apiBase: string): Promise<Partial<BannerConfig> | null> {
  if (!tid) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2000);
  try {
    const r = await fetch(`${apiBase}/api/policy/${encodeURIComponent(tid)}/banner-config`, { signal: controller.signal });
    if (!r.ok) return null;
    const d = (await r.json()) as { categories?: string[]; notice?: boolean; privacy_url?: string; cookie_url?: string; accent?: string; lang?: string };
    return {
      categories: Array.isArray(d.categories) ? d.categories.filter((c) => CATEGORIE_NOTE.includes(c)) : undefined,
      notice: d.notice === true,
      privacyUrl: typeof d.privacy_url === 'string' ? d.privacy_url : undefined,
      cookieUrl: typeof d.cookie_url === 'string' ? d.cookie_url : undefined,
      accent: typeof d.accent === 'string' ? d.accent : undefined,
      lang: typeof d.lang === 'string' && TESTI[d.lang] ? d.lang : undefined,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Unione: attributi del tag (se scritti) sopra la configurazione del CRM, sopra i valori di base. */
export function unisciConfigBanner(script: HTMLScriptElement | null, remota: Partial<BannerConfig> | null): BannerConfig {
  const attr = (nome: string) => script?.getAttribute(nome);
  const categorieAttr = attr('data-categories');
  const categorie = categorieAttr !== null && categorieAttr !== undefined
    ? categorieAttr.split(',').map((s) => s.trim().toLowerCase()).filter((s) => CATEGORIE_NOTE.includes(s))
    : (remota?.categories ?? []);
  const lingua = (attr('data-lang') ?? remota?.lang ?? document.documentElement.lang ?? 'it').slice(0, 2).toLowerCase();
  return {
    categories: Array.from(new Set(categorie)),
    notice: attr('data-notice') === 'true' || remota?.notice === true,
    lang: TESTI[lingua] ? lingua : 'it',
    privacyUrl: attr('data-privacy-url') ?? remota?.privacyUrl ?? '/privacy',
    cookieUrl: attr('data-cookie-url') ?? remota?.cookieUrl ?? '/cookie',
    accent: attr('data-accent') ?? remota?.accent ?? '#1a1a1a',
  };
}

export function bannerRichiesto(script: HTMLScriptElement | null): boolean {
  const globale = (window as unknown as { EMERGE_PRIVACY_BANNER?: { enabled?: boolean } }).EMERGE_PRIVACY_BANNER;
  return script?.getAttribute('data-banner') === 'true' || globale?.enabled === true;
}

export function leggiConfigBanner(script: HTMLScriptElement | null): BannerConfig | null {
  const globale = (window as unknown as { EMERGE_PRIVACY_BANNER?: Partial<BannerConfig> & { enabled?: boolean } }).EMERGE_PRIVACY_BANNER;
  const attivo = script?.getAttribute('data-banner') === 'true' || globale?.enabled === true;
  if (!attivo) return null;
  const lista = (script?.getAttribute('data-categories') ?? (globale?.categories ?? []).join(',') ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => CATEGORIE_NOTE.includes(s));
  const lingua = (script?.getAttribute('data-lang') ?? globale?.lang ?? document.documentElement.lang ?? 'it').slice(0, 2).toLowerCase();
  return {
    categories: Array.from(new Set(lista)),
    notice: script?.getAttribute('data-notice') === 'true' || globale?.notice === true,
    lang: TESTI[lingua] ? lingua : 'it',
    privacyUrl: script?.getAttribute('data-privacy-url') ?? globale?.privacyUrl ?? '/privacy',
    cookieUrl: script?.getAttribute('data-cookie-url') ?? globale?.cookieUrl ?? '/cookie',
    accent: script?.getAttribute('data-accent') ?? globale?.accent ?? '#1a1a1a',
  };
}

/** Esegue script, carica iframe e nasconde segnaposto delle categorie concesse. */
export function applicaBlocchi(categories: ConsentCategories): void {
  const concessa = (c: string | undefined) => Boolean(c && (c === 'necessary' || categories[c] === true));
  document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-emerge-category]').forEach((s) => {
    const cat = s.getAttribute('data-emerge-category') ?? undefined;
    if (!concessa(cat) || s.getAttribute('data-emerge-activated') === '1') return;
    const nuovo = document.createElement('script');
    Array.from(s.attributes).forEach((a) => {
      if (['type', 'data-emerge-category', 'data-emerge-activated'].includes(a.name)) return;
      nuovo.setAttribute(a.name, a.value);
    });
    nuovo.type = s.getAttribute('data-emerge-type') ?? 'text/javascript';
    if (!s.src) nuovo.text = s.textContent ?? '';
    s.setAttribute('data-emerge-activated', '1');
    s.parentNode?.insertBefore(nuovo, s.nextSibling);
  });
  document.querySelectorAll<HTMLElement>('[data-emerge-src][data-emerge-category]').forEach((el) => {
    const cat = el.getAttribute('data-emerge-category') ?? undefined;
    const src = el.getAttribute('data-emerge-src');
    if (concessa(cat) && src && el.getAttribute('src') !== src) el.setAttribute('src', src);
    else if (!concessa(cat)) el.removeAttribute('src');
  });
  document.querySelectorAll<HTMLElement>('[data-emerge-placeholder-for]').forEach((el) => {
    const cat = el.getAttribute('data-emerge-placeholder-for') ?? undefined;
    el.hidden = concessa(cat);
  });
}

export function avviaBanner(api: EmergePrivacyAPI, config: BannerConfig): void {
  const t = TESTI[config.lang];
  if (!t) throw new Error('unsupported_banner_language');
  const host = document.createElement('div');
  host.id = 'emerge-privacy-ui';
  if (config.publication) {
    host.setAttribute('data-policy-site-id', config.publication.site_id);
    host.setAttribute('data-release-id', config.publication.release_id);
    host.setAttribute('data-content-hash', config.publication.content_hash);
    host.setAttribute('data-consent-scope-hash', config.publication.consent_scope_hash);
    host.setAttribute('data-policy-lang', config.publication.lang);
  }
  host.style.setProperty('--accento', config.accent);
  const radice = host.attachShadow({ mode: 'open' });
  const stile = document.createElement('style');
  stile.textContent = STILE;
  radice.appendChild(stile);
  const statoRegistrazione = document.createElement('p');
  statoRegistrazione.setAttribute('role', 'status');
  statoRegistrazione.hidden = true;
  radice.appendChild(statoRegistrazione);
  let contenuto: HTMLElement | null = null;
  let ultimoFocus: HTMLElement | null = null;

  const monta = () => {
    if (!host.isConnected) (document.body ?? document.documentElement).appendChild(host);
  };
  const svuota = () => {
    if (contenuto) { contenuto.remove(); contenuto = null; }
  };
  const focusabili = (cont: HTMLElement) =>
    Array.from(cont.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
  const intrappola = (cont: HTMLElement) => (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const el = focusabili(cont);
    if (el.length === 0) return;
    const primo = el[0];
    const ultimo = el[el.length - 1];
    const attivo = radice.activeElement as HTMLElement | null;
    if (e.shiftKey && attivo === primo) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && attivo === ultimo) { e.preventDefault(); primo.focus(); }
  };

  const consenti = async (scelte: Record<string, boolean>, source: 'banner_first' | 'banner_update' | 'preferences') => {
    const categorie: ConsentCategories = { necessary: true };
    config.categories.forEach((c) => { categorie[c] = Boolean(scelte[c]); });
    svuota();
    statoRegistrazione.hidden = true;
    await api.updateConsent({ categories: categorie, source });
    if (api.getConsent()?.registration === 'unrecorded') {
      monta();
      statoRegistrazione.textContent = ERRORE_REGISTRAZIONE[config.lang];
      statoRegistrazione.hidden = false;
    }
  };

  const mostraBanner = () => {
    monta();
    svuota();
    const velo = document.createElement('div');
    velo.className = 'velo';
    velo.setAttribute('aria-hidden', 'true');
    const cornice = document.createElement('div');
    cornice.className = 'cornice';
    const scheda = document.createElement('div');
    scheda.className = 'scheda';
    scheda.setAttribute('role', 'dialog');
    scheda.setAttribute('aria-modal', 'true');
    scheda.setAttribute('aria-labelledby', 'emerge-consent-title');
    scheda.setAttribute('aria-describedby', 'emerge-consent-desc');
    scheda.innerHTML = `<h2 id="emerge-consent-title">${t.title}</h2><p id="emerge-consent-desc">${t.description(attributo(config.privacyUrl), attributo(config.cookieUrl))}</p>`;
    const bottoni = document.createElement('div');
    bottoni.className = 'bottoni';
    const crea = (testo: string, azione: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = testo;
      b.addEventListener('click', azione);
      bottoni.appendChild(b);
      return b;
    };
    const tutteTrue = Object.fromEntries(config.categories.map((c) => [c, true]));
    const tutteFalse = Object.fromEntries(config.categories.map((c) => [c, false]));
    const accetta = crea(t.acceptAll, () => { void consenti(tutteTrue, 'banner_first'); });
    crea(t.rejectAll, () => { void consenti(tutteFalse, 'banner_first'); });
    crea(t.preferences, () => mostraModale('banner'));
    scheda.appendChild(bottoni);
    cornice.appendChild(scheda);
    const involucro = document.createElement('div');
    involucro.appendChild(velo);
    involucro.appendChild(cornice);
    involucro.addEventListener('keydown', intrappola(scheda));
    radice.appendChild(involucro);
    contenuto = involucro;
    accetta.focus();
  };

  const mostraModale = (origine: 'banner' | 'link') => {
    monta();
    ultimoFocus = (document.activeElement as HTMLElement | null) ?? null;
    svuota();
    const stato = api.getConsent();
    const velo = document.createElement('div');
    velo.className = 'velo';
    velo.setAttribute('aria-hidden', 'true');
    velo.style.background = 'rgba(0,0,0,.5)';
    velo.style.zIndex = '10000';
    const cornice = document.createElement('div');
    cornice.className = 'cornice centro';
    const scheda = document.createElement('div');
    scheda.className = 'scheda modale';
    scheda.setAttribute('role', 'dialog');
    scheda.setAttribute('aria-modal', 'true');
    scheda.setAttribute('aria-labelledby', 'emerge-pref-title');
    const chiudi = document.createElement('button');
    chiudi.type = 'button';
    chiudi.className = 'chiudi';
    chiudi.setAttribute('aria-label', t.close);
    chiudi.innerHTML = '&times;';
    const titolo = document.createElement('h2');
    titolo.id = 'emerge-pref-title';
    titolo.textContent = t.modalTitle;
    scheda.appendChild(chiudi);
    scheda.appendChild(titolo);

    const scelte: Record<string, boolean> = {};
    const riga = (cat: string, fisso: boolean, inattiva = false) => {
      const r = document.createElement('div');
      r.className = inattiva ? 'categoria inattiva' : 'categoria';
      const testo = document.createElement('div');
      const nomeId = `emerge-cat-${cat}`;
      testo.innerHTML = `<strong id="${nomeId}">${t.categories[cat].name}${fisso ? ` (${t.always})` : ''}</strong><small>${t.categories[cat].help}</small>${inattiva ? `<small class="nota">${t.notUsed}</small>` : ''}`;
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'interruttore';
      sw.setAttribute('role', 'switch');
      sw.setAttribute('aria-labelledby', nomeId);
      const iniziale = fisso ? true : inattiva ? false : Boolean(stato?.categories[cat]);
      if (!inattiva) scelte[cat] = iniziale;
      sw.setAttribute('aria-checked', String(iniziale));
      if (fisso || inattiva) { sw.setAttribute('aria-disabled', 'true'); sw.disabled = true; }
      else sw.addEventListener('click', () => { scelte[cat] = !scelte[cat]; sw.setAttribute('aria-checked', String(scelte[cat])); });
      r.appendChild(testo);
      r.appendChild(sw);
      scheda.appendChild(r);
    };
    // Tutte le categorie note sono sempre elencate: quelle non in uso sul sito restano
    // disattivate con una nota, cosi' il cliente vede cosa il banner gestisce e cosa no.
    riga('necessary', true);
    CATEGORIE_NOTE.forEach((c) => riga(c, false, !config.categories.includes(c)));

    const azioni = document.createElement('div');
    azioni.className = 'azioni';
    const salva = document.createElement('button');
    salva.type = 'button';
    salva.textContent = t.save;
    salva.addEventListener('click', () => { void consenti(scelte, stato ? 'preferences' : 'banner_first'); });
    const accettaTutti = document.createElement('button');
    accettaTutti.type = 'button';
    accettaTutti.textContent = t.acceptAll;
    accettaTutti.addEventListener('click', () => { void consenti(Object.fromEntries(config.categories.map((c) => [c, true])), stato ? 'preferences' : 'banner_first'); });
    azioni.appendChild(salva);
    azioni.appendChild(accettaTutti);
    scheda.appendChild(azioni);

    const chiudiModale = () => {
      svuota();
      if (!api.getConsent() && origine === 'banner') mostraBanner();
      else ultimoFocus?.focus();
    };
    chiudi.addEventListener('click', chiudiModale);
    cornice.appendChild(scheda);
    const involucro = document.createElement('div');
    involucro.appendChild(velo);
    involucro.appendChild(cornice);
    involucro.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); chiudiModale(); return; }
      intrappola(scheda)(e);
    });
    radice.appendChild(involucro);
    contenuto = involucro;
    chiudi.focus();
  };

  // stato iniziale: blocchi applicati se c'e' gia' una scelta, banner se manca e ci sono categorie
  const stato: ConsentState | null = api.getConsent();
  applicaBlocchi(stato?.categories ?? { necessary: true });
  api.onConsentChange((s) => applicaBlocchi(s.categories));
  window.addEventListener('emerge-privacy-open-preferences', () => mostraModale('link'));
  // Il pulsante "Gestisci privacy" nel piede del sito: basta l'attributo, niente JavaScript.
  document.addEventListener('click', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-emerge-preferences]');
    if (el) { e.preventDefault(); mostraModale('link'); }
  });
  if (!stato && config.enabled !== false && (config.categories.length > 0 || config.notice)) {
    if (document.body) mostraBanner();
    else document.addEventListener('DOMContentLoaded', mostraBanner, { once: true });
  }
}
